import { useProjectLocalStore } from "./use-project-local-store";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PosterProject } from "PosterEditor/model/poster-project";
import { useCallback, useRef, useEffect } from "react";
import { useProjectRemoteStore } from "./use-project-remote-store";
import { useAuthContext } from "BEDROCK/auth/auth-provider";
import { migrateProject } from "PosterEditor/model/project-migrator";
import { ProjectTemplate } from "PosterEditor/model/project-template";
import { v4 as uuid } from "uuid";
import { useImageStore } from "entity-store/image-store/use-image-store";

export const useProjectStore = () => {
  const queryClient = useQueryClient();
  const { isUserAuthenticated, isLoading: authIsLoading } = useAuthContext();
  const {
    loadProjects: loadProjectsLocal,
    loadProject: loadProjectLocal,
    saveProject: saveProjectLocal,
    createProject: createProjectLocal,
    deleteProject: deleteProjectLocal,
    deleteAllProjects: deleteAllProjectsLocal,

    draftProject,
    existingDraft,
    deleteDraft,
    readProjectsLocal,
    replaceAllProjectsLocal,
    removeAllNotInCheckoutLocal,
  } = useProjectLocalStore();
  const {
    save: saveRemote,
    fetchAllProjects: fetchRemote,
    fetchProjectsInCheckout: fetchCheckoutProjectsRemote,
    remove: removeRemote,
    removeAllNotInCheckout: removeAllNotInCheckoutRemote,
    deleteDataAssociatedWithProject,
  } = useProjectRemoteStore();
  const { processPendingUploads } = useImageStore();

  const saveCooldownRef = useRef<NodeJS.Timeout | null>(null);
  const pendingProjectRef = useRef<PosterProject | null>(null);

  const {
    data: projects,
    refetch: refetchProjects,
    isLoading,
    isFetching,
    isError,
  } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => {
      if (isUserAuthenticated) {
        const remoteData = await fetchRemote();
        if (remoteData) {
          const migrated = remoteData.map((p) => migrateProject(p));

          // Preserve local-only projects that are never saved to Supabase:
          // - isTemplateDraft: drafts from template/Strava flows (excluded by saveProject guard)
          // - guest_project: pre-auth project awaiting migration in saveGuestProjectToUsersAccount
          // NOTE: Use readProjectsLocal() (not loadProjectsLocal()) to avoid a setProjects()
          // side-effect that would cause an intermediate render with stale data.
          const currentLocal = readProjectsLocal();
          const localOnly = currentLocal.filter(
            (p) => p.isTemplateDraft || p.id === "guest_project"
          );

          // Ghost cleanup: projects in localStorage that aren't on remote
          // and aren't local-only (drafts/guests) are orphaned. They're excluded from
          // the merged list below, which effectively removes them from localStorage.
          // Common cause: a remote save failed silently after saveDraft().
          const ghostProjects = currentLocal.filter(
            (p) => !p.isTemplateDraft && p.id !== "guest_project" && !migrated.find((m) => m.id === p.id)
          );
          if (ghostProjects.length > 0) {
            console.warn(`[Project Store] Cleaning up ${ghostProjects.length} ghost project(s) from localStorage`);
          }
          let merged = [...migrated, ...localOnly];

          // Preserve unsaved edits still in the debounce window.
          // Without this, a background refetch would overwrite localStorage with stale
          // Supabase data, silently losing the user's in-progress work.
          const pending = pendingProjectRef.current;
          if (pending) {
            const idx = merged.findIndex((p) => p.id === pending.id);
            if (idx !== -1) {
              merged[idx] = pending;
            } else {
              merged.push(pending);
            }
          }

          // Backup to sessionStorage before replacing localStorage
          // Keep only the last 3 backups to prevent quota issues
          // TODO(LHS-215): Evaluate if backup mechanism is still needed after production validation
          try {
            const backupKey = `projectStore_backup_${Date.now()}`;
            sessionStorage.setItem(backupKey, JSON.stringify(currentLocal));

            // Clean up old backups - keep only last 3
            const allKeys = Object.keys(sessionStorage);
            const backupKeys = allKeys
              .filter(k => k.startsWith('projectStore_backup_'))
              .sort()
              .reverse(); // Most recent first

            // Remove all but the 3 most recent
            backupKeys.slice(3).forEach(key => {
              try {
                sessionStorage.removeItem(key);
              } catch (e) {
                // Ignore errors during cleanup
              }
            });
          } catch (error) {
            console.warn('[Project Store] Failed to create sessionStorage backup:', error);
          }

          replaceAllProjectsLocal(merged);
          return merged;
        }
        // Defensive fallback for unexpected error cases
        console.warn('[Project Store] Unexpected: remote fetch returned falsy value — falling back to localStorage');
        return loadProjectsLocal();
      }
      // Unauthenticated: localStorage is the only store
      return loadProjectsLocal();
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    enabled: !authIsLoading, // Wait for auth to resolve before fetching
  });

  const loadProjects = (projectIDs: string[]) => {
    return projectIDs
      .map((projectId) => {
        return loadProjectLocal(projectId);
      })
      .filter((project) => project !== undefined) as PosterProject[];
  };

  const loadProject = (projectId: string) => {
    const project = loadProjectLocal(projectId);
    return project;
  };

  //TODO: proper implementation when backend is up.
  const fetchProject = async (id: string) => {
    return queryClient.fetchQuery({
      queryKey: ["project", id],
      queryFn: async () => await loadProjectLocal(id),
      staleTime: 1000 * 60 * 5,
    });
  };

  //TODO: proper implementation when backend is up.
  const putProject = useMutation({
    mutationFn: async (project: PosterProject) =>
      await saveRemote.mutateAsync(project),
  });

  const saveProject = useCallback(
    (project: PosterProject, forceImmediateRemoteStore?: boolean) => {
      const updated = { ...project, updatedAt: new Date() };
      saveProjectLocal(updated);
      queryClient.setQueryData(["projects"], (oldData?: PosterProject[]) => {
        // Merge instead of replace when oldData is undefined to avoid discarding existing projects
        if (!oldData) return [updated];
        // Check if project exists, update it or add it
        const existingIndex = oldData.findIndex((p) => p.id === updated.id);
        if (existingIndex >= 0) {
          return oldData.map((p) => (p.id === updated.id ? updated : p));
        }
        // Project doesn't exist, add it
        return [...oldData, updated];
      });
      if (!isUserAuthenticated) {
        return;
      }
      if (!project.isTemplateDraft) {
        pendingProjectRef.current = updated; // Track pending project
        if (forceImmediateRemoteStore) {
          // Clear any pending debounce — force save supersedes it
          if (saveCooldownRef.current) {
            clearTimeout(saveCooldownRef.current);
            saveCooldownRef.current = null;
          }
          saveRemote.mutate(updated);
          pendingProjectRef.current = null;
          return;
        }
        if (saveCooldownRef.current) {
          clearTimeout(saveCooldownRef.current);
        }
        saveCooldownRef.current = setTimeout(() => {
          saveRemote.mutate(updated);
          saveCooldownRef.current = null;
          pendingProjectRef.current = null;
        }, 2000);
      }
    },
    [saveRemote, queryClient, saveProjectLocal, isUserAuthenticated]
  );

  // Flush pending saves when page becomes hidden or before unload
  // NOTE: Effect depends only on [saveRemote] to avoid recreating listeners.
  // flushPendingSave uses saveRemote.mutate which is stable (won't change across renders).
  useEffect(() => {
    const flushPendingSave = () => {
      if (pendingProjectRef.current && saveCooldownRef.current) {
        clearTimeout(saveCooldownRef.current);
        saveCooldownRef.current = null;
        saveRemote.mutate(pendingProjectRef.current);
        pendingProjectRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        flushPendingSave();
      }
    };

    const handleBeforeUnload = () => {
      flushPendingSave();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [saveRemote]);

  const createProject = useMutation({
    mutationFn: async () => {
      const project = await createProjectLocal();
      await putProject.mutateAsync(project);
      return project;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });

  const loadDraftProject = async (createNew: boolean, template: ProjectTemplate) => {
    const draft = draftProject(createNew, template);
    // Wait for remote removal to complete before invalidating to prevent race condition
    await removeAllNotInCheckoutRemote(draft.id, projects ?? []);
    removeAllNotInCheckoutLocal(draft.id);
    // Use refetchQueries instead of invalidateQueries to wait for completion
    await queryClient.refetchQueries({ queryKey: ["projects"] });
    return draft;
  };

  const projectsNotInCheckout = (): PosterProject[] => {
    const filtered = projects?.filter(
      (p) => p.isInCheckout === false && p.isOrdered === false
    );
    return filtered ?? [];
  };

  const deleteProjectMutation = useMutation({
    mutationFn: async (project: PosterProject) => {
      // Only delete from remote if user is authenticated AND project is not guest_project
      if (isUserAuthenticated && project.id !== "guest_project") {
        await deleteDataAssociatedWithProject(project);
        await removeRemote.mutateAsync(project.id);
      }
      // Always delete from local storage
      await deleteProjectLocal(project);
    },
    onSuccess: async () => {
      // Force immediate refetch and wait for it to complete
      await queryClient.refetchQueries({ queryKey: ["projects"] });
    },
  });

  const deleteProject = (project: PosterProject) =>
    deleteProjectMutation.mutate(project);

  const deleteProjectAsync = (project: PosterProject) =>
    deleteProjectMutation.mutateAsync(project);

  const clearLocalCache = () => {
    deleteAllProjectsLocal();
  };

  const saveGuestProjectToUsersAccount = async () => {
    if (!isUserAuthenticated) {
      return;
    }
    const project = loadProject("guest_project");

    if (project) {
      const newID = uuid();
      console.log(`Migrating guest project to authenticated account. Old ID: guest_project, New ID: ${newID}`);

      // Process pending image uploads BEFORE saving the project
      // WARNING: If upload fails, project will reference non-existent images on remote
      try {
        await processPendingUploads(newID);
        console.log('Successfully processed pending image uploads');
      } catch (error) {
        console.error('Failed to process pending image uploads:', error);
        console.warn('Project may reference images that do not exist on remote. Images can be re-uploaded manually.');
        // Continue anyway - project will still save, but images will need manual re-upload
      }

      const updated = { ...project, id: newID, isTemplateDraft: false };
      // Delete the guest_project from local storage
      deleteProjectLocal(project);
      // Also delete any existing draft to ensure clean state
      deleteDraft();
      saveProject(updated, true);
      return updated.id;
    }

    console.warn('No guest project found to migrate');
    return;
  };

  const markProjectOrdered = (projectID: string) => {
    const project = loadProject(projectID);
    if (project) {
      const updated = { ...project, isOrdered: true };
      saveProject(updated, true);
    }
  };

  const removeAllNotInCheckout = async (exceptID: string | undefined) => {
    await removeAllNotInCheckoutRemote(exceptID, projects ?? []);
  };

  const {
    data: checkoutProjects,
    refetch: refetchCheckoutProjects,
    isLoading: isCheckoutLoading,
  } = useQuery({
    queryKey: ["checkoutProjects"],
    queryFn: async () => {
      if (!isUserAuthenticated) {
        return [];
      }
      return await fetchCheckoutProjectsRemote();
    },
    staleTime: 1000 * 60 * 2, // Cache for 2 minutes
    enabled: isUserAuthenticated,
  });

  return {
    // Cached projects list.
    // This is populated from remote store whn this hook is mounted.
    projects,

    // Checkout projects from Supabase
    checkoutProjects,
    refetchCheckoutProjects,
    isCheckoutLoading,

    draftProject: loadDraftProject,

    // Loads single project from local storage synchronously.
    // Use if you're sure the project is fetched with fetchProject.
    loadProject,

    // Loads multiple projects from local storage synchronously.
    // Use if you're sure the projects are fetched with fetchProject.
    loadProjects,

    // Local projects not in checkout.
    projectsNotInCheckout,

    // Loads single projct from remote and saves it locally.
    fetchProject,
    // Saves the project locally.
    saveProject,
    // Saves the project to remote asynchronously.
    putProject,
    // Async create function.
    createProject,
    // Async delete function.
    deleteProject,
    deleteProjectAsync,

    // Manual refresh trigger
    refetchProjects,

    existingDraft,
    deleteDraft,

    clearLocalCache,

    saveGuestProjectToUsersAccount,

    markProjectOrdered,

    isLoading,
    isFetching,
    isError,

    removeAllNotInCheckout,
  };
};
